import { Form, FormInstance } from "antd";
import { MutableRefObject, useEffect } from "react";
import { ApplicationDto } from "dtos/application.dto";
import { CustomStrategyConstants } from "utils/constants";
import StrategyPanelV2 from "../StrategyPanelV2";
import { StrategyFormValues } from "../functions";

export type AdvancedFormValues = { strategies: StrategyFormValues[] };

const noop = () => undefined;

/**
 * The original full editor (`StrategyPanelV2`) for a single strategy.
 * It keeps its own form shaped as `{ strategies: [strategy] }`, which is what the old panel expects;
 * the wizard reads it back through `formRef` when leaving advanced mode or saving.
 */
const AdvancedEditor = ({
  application,
  initialValues,
  formRef,
}: {
  application: ApplicationDto;
  initialValues: StrategyFormValues;
  formRef: MutableRefObject<FormInstance<AdvancedFormValues> | undefined>;
}) => {
  const [form] = Form.useForm<AdvancedFormValues>();
  const totalLawsuits = application.total_lawsuits ?? 0;
  const extraAmount =
    totalLawsuits > 0
      ? CustomStrategyConstants.lawsuit_initial * totalLawsuits
      : CustomStrategyConstants.insurance_one_time;

  useEffect(() => {
    formRef.current = form;
    return () => {
      formRef.current = undefined;
    };
  }, [form, formRef]);

  return (
    // Rendered inside the wizard's <form>, so this one must not output another <form> element.
    <Form
      form={form}
      component={false}
      layout="vertical"
      autoComplete="off"
      name="custom_strategy_advanced"
      initialValues={{ strategies: [initialValues] }}
    >
      <Form.List name="strategies">
        {(fields) =>
          fields.map(({ key, name }) => (
            <StrategyPanelV2
              key={key}
              name={name}
              application={application}
              totalDebt={application.enrolled_debt ?? 0}
              extraAmount={extraAmount}
              totalLawsuits={totalLawsuits}
              minWeeklyBudget={0}
              hasSplit={false}
              remove={noop}
              activeKey={undefined}
              setActiveKey={noop}
              setDisabled={noop}
            />
          ))
        }
      </Form.List>
    </Form>
  );
};

export default AdvancedEditor;
